import React, { lazy, Suspense, useEffect, useState } from 'react';
import Dropdown from './Dropdown';
import MobileDropdown from './MobileDropdown';
import { Link } from 'react-router-dom';
import { BannerConfig, DropdownItem, MenuItem } from '../common/types';
import SimpleButton from './SimpleButton';
import { useAppSelector } from '../redux/hooks';
import { HiMiniBellAlert } from './icons/reactIconShims';
import { defaultBannerConfig } from '../common/bannerDefaults';
import { getSiteGlobals } from '../utils/api';
import { preloadLikelyRoutes, preloadRoute } from '../utils/routePreload';

type NavItem = MenuItem | DropdownItem;
const ContactModal = lazy(() => import('./ContactModal'));

interface NavBarProps {
    logo: string;
    navItems: NavItem[];
    navCta?: { label?: string; href?: string; visible?: boolean };
    contactLink?: { label?: string; href?: string; visible?: boolean };
}

const NavBar: React.FC<NavBarProps> = ({ logo, navItems, navCta, contactLink }) => {
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [bannerConfig, setBannerConfig] = useState<BannerConfig>(defaultBannerConfig);
    const [showContactModal, setShowContactModal] = useState(false);
    const isNavbarTransparent = useAppSelector((state) => state.navbar.isTransparent);

    const handleScroll = () => {
        const offset = window.scrollY;
        setHasScrolled(offset > 0);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        let mounted = true;
        getSiteGlobals()
            .then((res) => {
                if (mounted && res?.data?.banner) {
                    setBannerConfig(res.data.banner);
                }
            })
            .catch(() => {
                if (mounted) setBannerConfig(defaultBannerConfig);
            });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const run = () => preloadLikelyRoutes();
        const win = window as any;
        const idleId = win.requestIdleCallback
            ? win.requestIdleCallback(run, { timeout: 2500 })
            : window.setTimeout(run, 1500);

        return () => {
            if (win.cancelIdleCallback && typeof idleId === 'number') {
                win.cancelIdleCallback(idleId);
            } else {
                window.clearTimeout(idleId);
            }
        };
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleDropdown = (label: string) => {
        if (openDropdown === label) {
            setOpenDropdown(null);
        } else {
            setOpenDropdown(label);
        }
    };

    const bannerLink = bannerConfig?.link || defaultBannerConfig.link;
    const secondaryLink = bannerConfig?.secondaryLink || defaultBannerConfig.secondaryLink;
    const showBanner = bannerConfig?.enabled !== false;
    const isExternalLink = bannerLink?.href?.startsWith('http');
    const isSecondaryExternal = secondaryLink?.href?.startsWith('http');
    const secondaryMessage = bannerConfig?.secondaryMessage || defaultBannerConfig.secondaryMessage || '';
    const linkGapPx = bannerConfig?.linkGapPx ?? defaultBannerConfig.linkGapPx ?? 50;
    const visibleNavItems = navItems.filter((item) => item.visible !== false);
    const cta = navCta || { visible: false };
    const showCta = cta.visible !== false && Boolean(cta.href || cta.label);
    const contact = contactLink || { visible: false };
    const showContactLink = contact.visible !== false && Boolean(contact.href && contact.label);
    const preloadInternalRoute = (href?: string) => {
        if (href?.startsWith('/')) preloadRoute(href);
    };

    return (
        <div className={`hcr{HiddenInHTML!?} fixed top-0 z-30 w-full text-white font-jakarta font-bold ${hasScrolled && 'shadow-md'}`}>
            {/* Upper bar */}
            {showBanner && (
                <div className='bg-ekc-blue text-sm bg-color-bar bg-no-repeat'>
                    <div className='container mx-auto px-4'>
                        <ul className="flex justify-between list-none m-0 py-3">
                            <li>
                                <div className='flex flex-wrap items-center gap-2 text-white font-normal py-2 px-3'>
                                    <HiMiniBellAlert className='fill-ekc-light-green text-lg' />
                                    <div className='flex flex-wrap items-center gap-2'>
                                        <span>{bannerConfig?.message || defaultBannerConfig.message}</span>
                                        {bannerLink?.type === 'modal' ? (
                                            <button
                                                type="button"
                                                onClick={() => setShowContactModal(true)}
                                                className='underline font-semibold hover:text-gray-200'
                                            >
                                                {bannerLink.label || ''}
                                            </button>
                                        ) : bannerLink?.href ? (
                                            isExternalLink ? (
                                                <a
                                                    href={bannerLink.href}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className='underline font-semibold hover:text-gray-200'
                                                >
                                                    {bannerLink.label || ''}
                                                </a>
                                            ) : (
                                                <Link
                                                    to={bannerLink.href}
                                                    onMouseEnter={() => preloadInternalRoute(bannerLink.href)}
                                                    onFocus={() => preloadInternalRoute(bannerLink.href)}
                                                    className='underline font-semibold hover:text-gray-200'
                                                >
                                                    {bannerLink.label || ''}
                                                </Link>
                                            )
                                        ) : null}
                                    </div>
                                    {(secondaryMessage || secondaryLink?.href || secondaryLink?.label) ? (
                                        <div
                                            className='flex flex-wrap items-center gap-2'
                                            style={{ marginLeft: `${linkGapPx}px` }}
                                        >
                                            <HiMiniBellAlert className='fill-ekc-light-green text-lg' />
                                            {secondaryMessage ? <span>{secondaryMessage}</span> : null}
                                            {secondaryLink?.type === 'modal' ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowContactModal(true)}
                                                    className='underline font-semibold hover:text-gray-200'
                                                >
                                                    {secondaryLink.label || ''}
                                                </button>
                                            ) : secondaryLink?.href ? (
                                                isSecondaryExternal ? (
                                                    <a
                                                        href={secondaryLink.href}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className='underline font-semibold hover:text-gray-200'
                                                    >
                                                        {secondaryLink.label || ''}
                                                    </a>
                                                ) : (
                                                    <Link
                                                        to={secondaryLink.href}
                                                        onMouseEnter={() => preloadInternalRoute(secondaryLink.href)}
                                                        onFocus={() => preloadInternalRoute(secondaryLink.href)}
                                                        className='underline font-semibold hover:text-gray-200'
                                                    >
                                                        {secondaryLink.label || ''}
                                                    </Link>
                                                )
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            </li>
                            {showContactLink ? <li className='lg:inline-block align-middle hidden'>
                                <Link
                                    to={contact.href || ''}
                                    onMouseEnter={() => preloadInternalRoute(contact.href)}
                                    onFocus={() => preloadInternalRoute(contact.href)}
                                    className='text-white py-2 px-3 transition-colors duration-500'
                                >
                                    {contact.label}
                                </Link>
                            </li> : null}
                        </ul>
                    </div>
                </div>
            )}

            {/* Navigation bar */}
            <nav className={`${!isNavbarTransparent || hasScrolled ? '!bg-custom-black' : 'bg-transparent'} ${isMobileMenuOpen ? "!bg-custom-black" : "bg-transparent"} transition duration-500 ease-in-out`}>
                <div className='container mx-auto px-4 lg:px-20 py-6'>
                    <div className='flex flex-wrap items-center justify-between'>
                        <div className='shrink-0'>
                            <Link to="/" onMouseEnter={() => preloadRoute('/')} onFocus={() => preloadRoute('/')}>
                                <picture>
                                    <source srcSet={logo} type="image/webp" />
                                    <img src={logo} alt="logo" className='max-h-5 sm:max-h-10' />
                                </picture>
                            </Link>
                        </div>
                        <div className='hidden lg:flex'>
                            <menu className='flex justify-end'>
                                <div className="list-none m-0 p-0">
                                    {visibleNavItems.map((item, index) => {
                                        if (item.type === 'link') {
                                            return (
                                                <div key={index} className='inline-block px-4'>
                                                    <Link
                                                        to={item.to}
                                                        onClick={item.onClick}
                                                        onMouseEnter={() => preloadRoute(item.to)}
                                                        onFocus={() => preloadRoute(item.to)}
                                                        className='text-white py-2 px-3 hover:text-gray-400 transition-colors duration-500'
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </div>
                                            );
                                        } else if (item.type === 'dropdown') {
                                            return (
                                                <Dropdown
                                                    key={index}
                                                    label={item.label}
                                                    leftColumn={item.leftColumn}
                                                    rightColumn={item.rightColumn}
                                                    clickableLabel={item.clickableLabel}
                                                    onPreload={preloadRoute}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                                {showCta && (
                                    <a
                                        href={cta.href || ''}
                                        target="_blank"
                                        rel="noreferrer"
                                        onMouseEnter={() => preloadInternalRoute(cta.href)}
                                        onFocus={() => preloadInternalRoute(cta.href)}
                                    >
                                        <SimpleButton
                                            label={cta.label || ''}
                                            textColor="text-white"
                                            bgColor="ekc-image-gradient"
                                            size="small"
                                            roundness="rounded-full"
                                        />
                                    </a>
                                )}
                            </menu>
                        </div>
                        {/* Hamburger button */}
                        <div className='block lg:hidden'>
                            <button
                                className='flex items-center px-3 py-2 border rounded text-gray-400 border-custom-gray hover:text-ekc-blue hover:border-ekc-blue'
                                onClick={toggleMobileMenu}
                            >
                                <svg
                                    className='fill-current h-3 w-3'
                                    viewBox='0 0 20 20'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <title>Menu</title>
                                    <path
                                        d='M0 0h20v20H0V0zm2 3h16v2H2V3zm0 5h16v2H2V8zm0 5h16v2H2v-2z'
                                        fillRule='evenodd'
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                {isMobileMenuOpen && <div className='w-full border-b border-gray-600 mx-auto' style={{ maxWidth: '90%' }}></div>}
            </nav>

            {/* Mobile menu */}
            <div className={`absolute top-full left-0 w-full bg-custom-black px-4 transition-opacity duration-500 ease-in-out overflow-y-auto max-h-screen ${isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                <ul className="flex flex-col items-start py-5">
                    {visibleNavItems.map((item, index) => {
                        if (item.type === 'link') {
                            return (
                                <li key={index} className='w-full text-left'>
                                    <Link
                                        to={item.to}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        onMouseEnter={() => preloadRoute(item.to)}
                                        onFocus={() => preloadRoute(item.to)}
                                        onTouchStart={() => preloadRoute(item.to)}
                                        className='block py-2 px-4 text-white transition-colors duration-500'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            );
                        } else if (item.type === 'dropdown') {
                            return (
                                <MobileDropdown
                                    key={index}
                                    label={item.label}
                                    leftColumn={item.leftColumn}
                                    rightColumn={item.rightColumn}
                                    isOpen={openDropdown === item.label}
                                    toggleDropdown={() => toggleDropdown(item.label)}
                                    closeMobileMenu={() => setIsMobileMenuOpen(false)}
                                    onPreload={preloadRoute}
                                />
                            );
                        }
                        return null;
                    })}
                    {showContactLink ? <li className='w-full text-left'>
                        <Link
                            to={contact.href || ''}
                            onClick={() => setIsMobileMenuOpen(false)}
                            onMouseEnter={() => preloadInternalRoute(contact.href)}
                            onFocus={() => preloadInternalRoute(contact.href)}
                            onTouchStart={() => preloadInternalRoute(contact.href)}
                            className='block py-2 px-4 text-white transition-colors duration-500'
                        >
                            {contact.label}
                        </Link>
                    </li> : null}
                </ul>
            </div>
            {showContactModal ? (
                <Suspense fallback={null}>
                    <ContactModal onClose={() => setShowContactModal(false)} />
                </Suspense>
            ) : null}
        </div>
    );
};

export default NavBar;
